import { GConstructor } from 'types';
import { PLAYER_DEFAULT_VOLUME } from 'utils/constants';
import CountdownLatch from 'utils/CountdownLatch';
import { clamp } from 'utils/math';
import {
  BaseAudioSegmentPlayer,
  BaseMediaElementAudioPlayer,
  ISegmentable,
} from './types';

const SEEK_EPSILON_SEC = 1;

// Mixin to play audio segments.
//
// This player operates under the following rules:
//  1. Audio cannot be preloaded in any way (audio tag's preload property must be
//     set to "none").  This is a critical IAB constraint that is important to this app
//  2. Audio must be played immediately when play() is called.  This is a browser
//     "autoplay" issue where the underlying media must be played in an event
//     handler triggered by a user-interaction (and not, for example, after some
//     asynchronous task).
//
// Given these constraints, initial playback works as follows:
//  1. When player is initialized, no data is downloaded (it is tied to an audio
//     element with preload="none")
//  2. When the user clicks play, the audio source is muted and immediately begins
//     playback from time 0
//  3. Audio data starts downloading once playback begins. Once we have the metadata,
//     we seek to the initial start time (some browsers, like Safari, won't even
//     perform the seek operation until metadata is downloaded)
//  4. Once we reach the initial start time, the underlying audio is unmuted

export default function Segmentable<
  TBase extends GConstructor<
    BaseMediaElementAudioPlayer & BaseAudioSegmentPlayer
  >,
>(Base: TBase): GConstructor<ISegmentable> & TBase {
  return class extends Base {
    _initialized: boolean;

    _initializedPromise: Promise<void>;

    _initialVolume: number;

    _isEnded: boolean;

    // A mixin class must have a constructor with a single rest parameter of type 'any[]'.ts(2545)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(...args: any[]) {
      super(...args);

      this._initialVolume = PLAYER_DEFAULT_VOLUME;
      this._initialized = false;
      this._isEnded = false;

      // set the volume to 0 initially and when seeked to the segment star time,
      // unmute it.  Different implementations were tested (see releases
      // 3.1.1, 3.1.2, 3.1.3) and some odd behavior was noticed in Safari. At first,
      // the audio was unmuted when playback was within the segment.  This was accomplished
      // by checking the playback time in the timeupdate callback.  Occasionally,
      // particularly when the browser was under load, audio was heard starting from
      // 0 (for up to a few seconds) and then seeking to the segment start time.  It was
      // observed that the currentTime in the timeupdate would be >= to the segment start
      // time, but the correct segment would only play after the "seeked" event was fired.
      // bizzarely, it looked something like this:
      //  - segment start at 10 sec
      //  - timeupdate event showed currentTime of 10 sec
      //  - time would pass (a few ms or even a few sec)
      //  - seeked event would fire with currentTime showing 10 sec
      //
      // in order to fix the audio initialy playing from 0, it remains muted until
      // we are in the segment, as indicated by the "seeked" event.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      super.volume = 0;

      this._initializedPromise = this.waitUntilInitialized();
      this.on('timeupdate', this.handleTimeupdate);

      this._initializedPromise.then(() => {
        if (this.segment.endSec === Infinity) {
          this.segment.endSec = this.element.duration;
        }
      });
    }

    private isWithinSegment() {
      return (
        Math.abs(super.currentTime() - this.segment.startSec) <=
          SEEK_EPSILON_SEC ||
        (super.currentTime() >= this.segment.startSec &&
          super.currentTime() <= this.segment.endSec)
      );
    }

    private waitUntilInitialized() {
      return new Promise<void>((resolve) => {
        const initializationLatch = new CountdownLatch(2);
        initializationLatch.onZero().then(() => {
          this._initialized = true;
          this.volume = this._initialVolume;
          resolve();
        });

        const handleSeeked = () => {
          if (this.isWithinSegment()) {
            initializationLatch.countDown();
            this.off('seeked', handleSeeked);
          }
        };

        const handleCanPlay = () => {
          if (this.isWithinSegment()) {
            initializationLatch.countDown();
            this.off('canplay', handleCanPlay);
          }
        };

        const handleLoadedMetadata = () => {
          super.seek(this.segment.startSec);
          this.off('loadedmetadata', handleLoadedMetadata);
        };

        this.on('seeked', handleSeeked);
        this.on('canplay', handleCanPlay);
        this.on('loadedmetadata', handleLoadedMetadata);
      });
    }

    private handleTimeupdate = () => {
      if (this.currentTime() >= this.duration && !this._isEnded) {
        // element.dispatch happens outside of the normal event loop.  we need
        // the order of events to simulate that of a full audio file reaching the
        // end - which is "pause" followed by "ended".  we have to wait for
        // "pause" to fire before dispatching "ended" because the two separate
        // event mechanisms can result in them getting delivered out of order
        const handleEnded = () => {
          this.element.dispatchEvent(new Event('ended'));
          this.off('pause', handleEnded);
        };
        this.on('pause', handleEnded);
        this.pause();
        this._isEnded = true;
      }
    };

    public play() {
      if (this.isWithinSegment()) {
        this._isEnded = false;
      }

      return super.play();
    }

    public seek(seconds: number) {
      super.seek(
        clamp(
          seconds + this.segment.startSec,
          this.segment.startSec,
          this.segment.endSec,
        ),
      );
    }

    public currentTime() {
      return super.currentTime() - this.segment.startSec;
    }

    // returns the current time within the full audio source as opposed to
    // currentTime() which gets the current time within the audio segment
    public rawCurrentTime() {
      return super.currentTime();
    }

    public get duration() {
      return this.segment.endSec - this.segment.startSec;
    }

    public get isReady() {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return super.isReady && this._initialized;
    }

    public async ready() {
      await super.ready();
      await this._initializedPromise;
    }

    public set volume(value: number) {
      if (!this._initialized) {
        this._initialVolume = value;
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        super.volume = value;
      }
    }
  };
}

export function isSegmentable<
  TBase extends GConstructor<
    BaseMediaElementAudioPlayer & BaseAudioSegmentPlayer
  > = GConstructor<BaseMediaElementAudioPlayer & BaseAudioSegmentPlayer>,
>(val: unknown): val is ISegmentable & TBase {
  return (val as ISegmentable & TBase).rawCurrentTime !== undefined;
}
